import { Component, OnInit, Input, ViewChild, EventEmitter, ChangeDetectorRef, signal } from '@angular/core';
import { CellClickEvent, GridComponent, SelectableSettings, DataStateChangeEvent, RowClassArgs, RowArgs, GroupKey, ColumnVisibilityChangeEvent, GridModule, ExcelModule, ColumnReorderEvent, GridDataResult } from "@progress/kendo-angular-grid";
import { State, AggregateDescriptor, CompositeFilterDescriptor, GroupDescriptor, GroupResult } from '@progress/kendo-data-query';
import { ContextMenuItem } from '../../interfaces/contex-menu-item.interface';
import { Router } from '@angular/router';
import { fileExcelIcon, clipboardTextIcon } from "@progress/kendo-svg-icons";
import { ColumnSetting } from '../../interfaces/column-setting.interface';
import { ColumnMenuSettings, PagerSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { FormControl, FormGroup } from '@angular/forms';
import { GridSettings } from '../../interfaces/grid-settings.interface';
import { Clipboard } from '@angular/cdk/clipboard';
import { TooltipDirective, TooltipModule } from '@progress/kendo-angular-tooltip';
import { SearchPostDataService } from '../../services/search-post-data.service';
import { GridDataBindingDirective } from '../../directives/grid-data-binding.directive';
import { UiColumn } from '../../ui/ui-column';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { TranslateModule } from '@ngx-translate/core';
import { GridAggregatePipe } from '../../pipes/grid-aggregate.pipe';
import { GridCellFormatPipe } from '../../pipes/grid-cell-format.pipe';
import { GridDetailComponent } from '../grid-detail/grid-detail.component';
import { CheckBoxModule } from '@progress/kendo-angular-inputs';
import { ContextMenuModule, ContextMenuComponent, ContextMenuSelectEvent } from '@progress/kendo-angular-menu';
import { RemoteSearchService } from '@core/services/remote-search.service ';
import { Observable } from 'rxjs';
import { INotificationService } from '../../services/inotification.service';

@Component({
    selector: 'app-grid',
    imports: [
      GridDataBindingDirective,
      GridModule,
      TooltipModule,
      ButtonModule,
      TranslateModule,
      GridAggregatePipe,
      GridCellFormatPipe,
      ExcelModule,
      ContextMenuModule,
      GridDetailComponent,
      CheckBoxModule
    ],
    providers: [],
    templateUrl: './search-grid.component.html',
    styleUrls: ['./search-grid.component.css']
})
export class SearchGridComponent implements OnInit {

  @ViewChild(GridDataBindingDirective) db: GridDataBindingDirective;
  @ViewChild('searchGrid') public grid: GridComponent;
  @ViewChild("gridMenu") public gridContextMenu: ContextMenuComponent;
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;

  @Input() apiController: string;
  @Input() data: Array<any> = [];
  @Input() keyFieldName: string;
  @Input() gridSettings: GridSettings<any>;

  public originalColumns: UiColumn[];

  public colSettings: ColumnSetting[] = [];
  public pagerSettings: PagerSettings;
  public pageSize: number = 50;
  public selectableSettings: SelectableSettings = { mode: "multiple", drag: true };
  public menuSettings: ColumnMenuSettings = { sort: true, columnChooser: true };
  public state: State = { skip: 0, take: 50, group: [], sort: [] };
  public expandedGroupKeys: GroupKey[] = [];
  public aggregates: AggregateDescriptor[] = [];
  public defaultRowCallback: (context: RowClassArgs) => { [k: string]: any };
  public defaultDoubleClick: (e: MouseEvent) => void;
  public isEditingEnabled: boolean = false;
  public exportFilename: string;
  public filter: CompositeFilterDescriptor = {
    logic: "and",
    filters: []
  };

  // Properties for row selection
  public mySelection: number[] = [];
  public lastSelectedRowIndex: number;
  public lastSelectedColumnName: number;
  public lastPointerEvent: PointerEvent;
  public lastSelectedItem: any;
  public isCheckBoxAllChecked: boolean = false;

  ElapsedTime = signal("");
  time = signal(0);
  interval: NodeJS.Timeout;
  editFormGroup: FormGroup | undefined;

  private currentEditRowIndex: number | undefined;

  constructor(
    private router: Router,
    private clipboard: Clipboard,
    private postDataService: SearchPostDataService,
    private cd: ChangeDetectorRef,
    private remote: RemoteSearchService,
    private notificationService: INotificationService
  ) {
    this.fetchAllData = this.fetchAllData.bind(this);
  }

  ngOnInit(): void {

    this.originalColumns = this.gridSettings.columns;
    this.originalColumns.forEach(oc => oc.visibleIndex = this.originalColumns.indexOf(oc) + 1); // 0 index is the checkbox column
    this.mapColumns();
    this.gridSettings.selectedRowChange = new EventEmitter<any>();
    this.gridSettings.selectedRowsChange = new EventEmitter<any>();

    this.exportFilename = this.apiController + "s " + (new Date().toDateString()) + ".xlsx";
    this.isEditingEnabled = this.colSettings.filter(c => c.editor !== undefined).length > 0;

    this.pagerSettings = {
      previousNext: true,
      position: "bottom",
      info: true,
      type: "input",
      pageSizes: [50, 100, 500]
    };

    if (this.gridSettings !== undefined && this.gridSettings.styleRow !== undefined && this.gridSettings.styleRow !== null) {
      this.defaultRowCallback = this.gridSettings.styleRow;
    } else {
      this.defaultRowCallback = this.rowCallback;
    }

    if (this.gridSettings !== undefined && this.gridSettings.doubleClick !== undefined && this.gridSettings.doubleClick !== null) {
      this.defaultDoubleClick = this.gridSettings.doubleClick;
    } else {
      this.defaultDoubleClick = this.onDblClicked;
    }

    if (!this.gridSettings.contextMenuItems) {
      this.gridSettings.contextMenuItems = [];
    }


    // add excel menu item
    var excel: ContextMenuItem = {
      title: "Export to Excel",
      iconClass: fileExcelIcon,
      action: this.exportToExcel.bind(this)
    };

    // add copy text menu item
    var copyText: ContextMenuItem = {
      title: "Copy Text",
      iconClass: clipboardTextIcon,
      action: this.copyText.bind(this),
    }

    if (this.gridSettings.contextMenuItems.length > 0) {
      this.gridSettings.contextMenuItems?.push({ separator: true });
    }

    this.gridSettings.contextMenuItems?.push(copyText);
    this.gridSettings.contextMenuItems?.push({ separator: true });
    this.gridSettings.contextMenuItems?.push(excel);

    this.cd.detectChanges();
  }

  ngAfterViewInit(): void {

    var gridBody = document.querySelector('#' + this.gridSettings.gridName + ' tbody');
    if (gridBody) {
      gridBody.addEventListener('click', () => {
        this.lastPointerEvent = window.event as PointerEvent;
      });
    }

    document.addEventListener('click', (e) => {
      this.checkboxListener(e);
    });

    var checkBoxAll = document.getElementById('k-grid0-select-all');
    if (checkBoxAll) {
      checkBoxAll.addEventListener('click', () => {
        // need to select all rows
        if (this.isCheckBoxAllChecked) {
          this.mySelection = [];
        } else {
          if (this.data.length > 0) {
            var numDataIndexes = this.data.length - 1;
            var indexesToAdd = this.range(0, numDataIndexes);
            Array.prototype.push.apply(this.mySelection, indexesToAdd);
          }
        }
        this.isCheckBoxAllChecked = !this.isCheckBoxAllChecked;
      });
    }
  }

  checkboxListener(ev: MouseEvent): void {
    var target = ev?.target;
    if (target) {
      var element = target as HTMLElement;
      var id = element.id;
      if (id.startsWith("k-grid") && id.includes("checkbox")) {
        var index = parseInt(id.split('checkbox')[1]);
        this.mySelection = [];
        this.mySelection.push(index);
      }
    }
  }
    
  copyText(): void {
    var value: string = "";
    if (this.lastSelectedItem && this.lastSelectedColumnName) {
      value = this.lastSelectedItem[this.lastSelectedColumnName];
    }

    this.clipboard.copy(value);
  }

  exportToExcel(params: any[]): void {
    this.grid.saveAsExcel();
  }

  public fetchAllData = (): Observable<GridDataResult> => {
    var tempState: State = JSON.parse(JSON.stringify(this.state));
    var remote = Reflect.get(this.db, 'remote');
    var value = Reflect.get(remote, 'value');
    tempState.take = value.total;
    var postData = this.postDataService.getPostData(tempState.sort, tempState.filter);
    return this.remote.fetch(this.apiController, tempState, postData, this.gridSettings.columns);
  }

  mapColumns() {
    this.colSettings = [];
    for (var i = 0; i < this.gridSettings.columns.length; i++) {
      var column = this.gridSettings.columns[i];
      const col: ColumnSetting =
      {
        field: column.field,
        title: column.title,
        width: column.size,
        type: column.type,
        hidden: column.hidden,
        aggregate: column.aggregate,
        editor: column.editor,
        editable: column.editor !== undefined,
        format: column.format,
        filterFormat: column.filterFormat,
        hasActions: column.hasActions,
        actions: column.actions,
        convertDate: column.convertDate
      }
      this.colSettings.push(col);
      if (col.aggregate !== undefined) {
        this.aggregates.push({ field: col.field, aggregate: col.aggregate });
      }
    }

    var groupedColumns = this.gridSettings.columns.filter(c => c.groupIndex != undefined && c.groupIndex > 0).sort((a, b) => this.sortByGroupIndex(a, b));
    var groups: GroupDescriptor[] = [];
    groupedColumns.forEach(gc => groups.push({ field: gc.field, aggregates: [{ aggregate: "count", field: gc.field }] }));
    if (groups.length > 0) {
      this.state.group = groups;
      this.state.take = 100000;
      this.pageSize = 100000;
    }
  }

  sortByGroupIndex(a: UiColumn, b: UiColumn): number {
    if (a.groupIndex == undefined) a.groupIndex = 0;
    if (b.groupIndex == undefined) b.groupIndex = 0;

    if (a.groupIndex < b.groupIndex) {
      return -1;
    }
    if (a.groupIndex > b.groupIndex) {
      return 1;
    }
    return 0;
  }

  trackByColSetting(index: number, item: ColumnSetting): string {
    return item.field;
  }

  showLoadingProgress() {
    var loadingContainer = document.getElementById('loading');
    if (loadingContainer) {
      this.startTimer();
      loadingContainer.style.display = "block";
    }
  }

  hideLoadingProgress() {
    var loadingContainer = document.getElementById('loading');
    if (loadingContainer) {
      this.clearTimer();
      loadingContainer.style.display = "none";
    }
  }

  startTimer() {
    this.interval = setInterval(() => {
      var t = this.time();
      this.time.set(t + 1);
      this.ElapsedTime.set(this.transform(this.time()));
    }, 1000);
  }

  clearTimer() {
    this.time.set(0);
    this.ElapsedTime.set("");
    clearInterval(this.interval);
  }

  transform(value: number): string {
    var humanizedTime: string = "";
    const tempTime: number = value;

    const hours: number = Math.floor(value / 3600);
    const minutes: number = Math.floor(value % 3600 / 60);
    const seconds: number = Math.floor(value % 3600 % 60);

    if (hours > 0) {
      if (hours == 1) {
        humanizedTime += " " + hours + " hour";
      } else {
        humanizedTime += " " + hours + " hours";
      }
    }

    if (minutes > 0) {
      if (minutes == 1) {
        humanizedTime += " " + minutes + " minute";
      } else {
        humanizedTime += " " + minutes + " minutes";
      }
    }

    if (seconds > 0) {
      if (seconds == 1) {
        humanizedTime += " " + seconds + " second";
      } else {
        humanizedTime += " " + seconds + " seconds";
      }
    }

    return humanizedTime;
  }

  showTooltip(e: MouseEvent): void {
    e.stopPropagation();
    const element = e.target as HTMLElement;
    if (element &&
      (element.nodeName === "TD" || element.className === "k-column-title") &&
      element.offsetWidth < element.scrollWidth
    ) {
      this.tooltipDir.toggle(element);
    } else {
      this.tooltipDir.hide();
    }
  }


  selectRow(event: SelectionEvent): void {
    if (event.selectedRows != null) {
      for (let i = 0; i < event.selectedRows.length; i++) {
        if (!this.mySelection.includes(event.selectedRows[i].index)) {
          this.mySelection.push(event.selectedRows[i].index);
        }
      }
    }
  }

  onContextMenuSelect(e: ContextMenuSelectEvent) {
    if (e.item && e.item.data.action) {
      e.item.data.action(e.item.data.params);
    }
  }

  onCellClicked(e: CellClickEvent): void {
    this.gridSettings.selectedRow = e.dataItem;
    this.gridSettings.selectedRowChange?.emit(this.gridSettings.selectedRow);

    // Handle Row Selection
    // We need to do custom row selection logic because selecting a row by right-click is not supported.
    // This will handle single row selection, and multi selection using the shift key or ctrl key.
    if (e.rowIndex != undefined) {
      var firstRowIndex = this.lastSelectedRowIndex;
      var lastRowIndex = e.rowIndex;

      var rightClick = window.event !== undefined && (window.event as PointerEvent).which == 3;
      var multiSelectWithCtrl = this.lastPointerEvent != null && this.lastPointerEvent.ctrlKey;
      var multiSelectWithShift = this.lastPointerEvent != null && this.lastPointerEvent.shiftKey;

      if (this.lastPointerEvent == undefined && window.event !== undefined && window.event instanceof PointerEvent) {
        this.lastPointerEvent = window.event;
      }

      if (rightClick) {
        if (!this.mySelection.includes(lastRowIndex)) {
          this.mySelection.push(lastRowIndex);
        }
      } else if (multiSelectWithShift) {
        var selectedIndexes = this.range(firstRowIndex, lastRowIndex);
        this.mySelection = [];
        Array.prototype.push.apply(this.mySelection, selectedIndexes);
      } else if (multiSelectWithCtrl) {
        this.mySelection.push(lastRowIndex);
      } else {
        this.mySelection = [];
        this.mySelection.push(lastRowIndex);
      }
    }

    var dataResult: any[] = (this.grid.data as GridDataResult).data;

    this.gridSettings.selectedRows = this.mySelection.map(idx => {
      if (this.data && this.data.length > 0) {
        return this.data.find(item => this.data.indexOf(item) == idx);
      }
      if (dataResult && dataResult.length > 0) {
        if (this.state.group && this.state.group.length > 0) {
          var allData = dataResult.map(dr => dr.items).reduce((accumulator, value) => accumulator.concat(value), []);;
          return allData[idx];
        } else {
          return dataResult.find(item => dataResult.indexOf(item) == idx);
        }
      }
    });
    this.gridSettings.selectedRowsChange?.emit(this.gridSettings.selectedRows);

    this.lastSelectedRowIndex = e.rowIndex;
    this.lastSelectedColumnName = e.column.field;
    this.lastSelectedItem = e.dataItem;
    
    if (this.isEditingEnabled) {
      if (e.isEdited || (this.editFormGroup && !this.editFormGroup.valid)) {
        return;
      }

      this.saveEditor();

      this.editFormGroup = this.createFormGroup(e.dataItem);
      this.currentEditRowIndex = e.rowIndex;

      this.grid.editRow(e.rowIndex, this.editFormGroup);
    }

    if (e.type == "contextmenu") {
      e.originalEvent.preventDefault();

      this.gridContextMenu.show({
        left: e.originalEvent.pageX,
        top: e.originalEvent.pageY
      });
    }
  }

  private range(start: number, end: number): number[] {
    if (end > start) {
      return Array
        .apply(null, Array((end - start) + 1))
        .map(function (_, n) { return n + start; }); 
    } else {
      return Array
        .apply(null, Array((start - end) + 1))
        .map(function (_, n) { return n + end; }); 
    }
  }

  private closeEditor(): void {
    this.grid.closeRow(this.currentEditRowIndex);

    this.currentEditRowIndex = undefined;
    this.editFormGroup = undefined;
  }

  private saveEditor(): void {
    if (this.editFormGroup) {
      // save values
      this.closeEditor();
    }
  }

  createFormGroup(dataItem: any): FormGroup {
    var form: { [k: string]: any } = {};

    for (var key in dataItem) {
      var colSetting = this.colSettings.find(c => c.field == key);
      if (colSetting && colSetting.editable) {
        form[key] = new FormControl(dataItem[key]);
      }
    }

    return new FormGroup(form);
  }

  onDblClicked(e: MouseEvent) {
    if (this.gridSettings.selectedRow) {

      var td = e.target as HTMLTableCellElement;

      var tableBody = document.querySelector("#" + this.gridSettings.gridName + " tbody");

      var canContinue: boolean = false;

      if (td && tableBody) {
        canContinue = tableBody.contains(td);
      }

      if (canContinue) {
        var entityID = this.gridSettings.selectedRow[this.keyFieldName];
        if (entityID) {
          var endpoint: string = "/" + this.apiController + "/Detail/" + entityID;

          var routerUrl: string = this.router.url;
          let count = routerUrl.split('/').length - 1;
          if (count > 1) {
            var split = routerUrl.split('/');
            routerUrl = '/' + split.slice(2).join('/');
          }

          // Open detail pages in new tab
          var baseUrl = window.location.href.replace(routerUrl, ''); //this.router.url
          window.open(baseUrl + endpoint, '_blank');
        }
      }
    }
  }

  public showDetailGrid(dataItem: any): boolean {
    var input = document.getElementById('showDetailGrid') as HTMLInputElement;
    if (input) {
      return JSON.parse(input.value);
    }

    return false;
  }

  columnVisibilityChange(e: ColumnVisibilityChangeEvent): void {
    var columns = e.columns;
    for (var i = 0; i < columns.length; i++) {
      var field = Reflect.get(columns[i], 'field');
      var matchingUiColumn = this.gridSettings.columns.filter(c => c.field == field)[0];
      matchingUiColumn.hidden = columns[i].hidden;
    }
  }

  getColumnTitle(f: string): string {
    return this.colSettings.filter(c => c.field == f)[0].title;
  }

  getGroupItemCount(group: GroupResult): number {

    var itemCount: number = group.items.length;

    try {

      var items: any[] = [];

      group.items.forEach((o: any) => {
        this.recursiveSearch(o, items);
      });

      itemCount = items.length;

    } catch { }

    return itemCount;
  }

  recursiveSearch(o: any, items: any[]): void {
    if (o['aggregates']) {
      this.recursiveSearch(o['items'], items);
    } else if (o instanceof Array && o[0]['aggregates']) {
      for (var i = 0; i < o.length; i++) {
        var r = o[i];
        this.recursiveSearch(r, items);
      }
    } else {
      items.push(...o);
    }
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.state = state;
    var count: number = (!this.state.group || this.state.group.length == 0) ? (!this.state.take ? 50 : (this.state.take > 500 ? 50 : this.state.take)) : 50;
    this.pageSize = count;
    this.state.take = count;
    if (this.state.group && this.state.group.length > 0) {
      this.pageSize = 100000;
      this.state.take = 100000;
      this.state.group.forEach((x: GroupDescriptor) => {
        x.aggregates = [{ field: x.field, aggregate: "count" }];
      });
      this.cd.detectChanges();
    }
    var remote = Reflect.get(this.db, 'remote');
    var value = Reflect.get(remote, 'value');
    var skipSearch = (this.state.filter && this.state.filter.filters.length > 0) != undefined && (value.total != undefined && value.total == 0);
    if (skipSearch) {
      this.notificationService.show({ type: "warning", title: "Please use the search bar on the left before filtering a column" });
      return;
    }

    this.db.bind(this.postDataService.getPostData(this.state.sort, this.state.filter), this.apiController, this, this.gridSettings.columns, this.state);
  }

  public onColumnReorder(event: ColumnReorderEvent) {
    // Telerik has confirmed a bug with column reordering not working while using virtual columns, this is their temporary work-around
    // https://www.telerik.com/account/support-center/view-ticket/1677223
    event.column.width += 1;
    setTimeout(() => {
      event.column.width -= 1;
    });
  }

  public rowCallback(context: RowClassArgs): { [k: string]: any } {
    return {};
  }

  public isRowSelected = (e: RowArgs): boolean =>
    this.mySelection.indexOf(e.index) >= 0;

}
